/**
 * Wave-0 §2: muzzle + impact VFX, re-toned to cold matte ordnance.
 *
 * TONE FIX (highest priority): the BODY of every effect is NormalBlending +
 * toneMapped:true so it sits inside the ACES grade and never crosses the 1.0
 * bloom threshold (AAA-21 — only Sol blooms). A single tiny AdditiveBlending core
 * (≤~25% footprint) carries the hot center only — never the halo. Palette is
 * ash-white → cold steel with a restrained ember center; hostile fire reads as a
 * dimmer red-grey by VALUE, not neon. The glow carrier is killed in the gradient
 * (alpha → 0 by ~35% radius, peak ≤0.7) → a tight matte puff, not a soft glow.
 */
import * as THREE from 'three';
import { type Vec3, vec3 } from '@blackmarble/sim-core';

const MUZZLE_LIFE = 0.08;
const IMPACT_LIFE = 0.25;
const MUZZLE_SIZE_KM = 0.28;
const IMPACT_SIZE_KM = 0.32;
const EMBER_CORE = 0xb98a52; // restrained ember — the very center only

type FxSlot = {
  readonly body: THREE.Sprite;
  readonly bodyMat: THREE.SpriteMaterial;
  readonly core: THREE.Sprite;
  readonly coreMat: THREE.SpriteMaterial;
  readonly sizeKm: number;
  readonly kind: 'muzzle' | 'impact';
  ttl: number;
  life: number;
  jitter: number; // ±15% per-shot scale
  variant: number; // 0..2 surface-feel variants (impact)
  worldPos: Vec3;
};

const hash = (n: number): number => {
  const s = Math.sin(n * 12.9898) * 43758.5453;
  return s - Math.floor(s);
};

/** Tight matte puff: peak alpha 0.7, fully transparent by ~35% radius. */
const createPuffTexture = (): THREE.CanvasTexture => {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return new THREE.CanvasTexture(canvas);
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,0.7)');
  g.addColorStop(0.22, 'rgba(255,255,255,0.38)');
  g.addColorStop(0.35, 'rgba(255,255,255,0.0)');
  g.addColorStop(1, 'rgba(255,255,255,0.0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
};

/** Tiny hot center for the single allowed additive core. */
const createCoreTexture = (): THREE.CanvasTexture => {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return new THREE.CanvasTexture(canvas);
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,0.9)');
  g.addColorStop(0.4, 'rgba(255,255,255,0.22)');
  g.addColorStop(1, 'rgba(255,255,255,0.0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
};

const createBodyMaterial = (map: THREE.Texture): THREE.SpriteMaterial =>
  new THREE.SpriteMaterial({
    map,
    transparent: true,
    depthWrite: false,
    blending: THREE.NormalBlending,
    toneMapped: true,
  });

const createCoreMaterial = (map: THREE.Texture): THREE.SpriteMaterial =>
  new THREE.SpriteMaterial({
    map,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: true,
  });

const createSlot = (
  parent: THREE.Group,
  puff: THREE.Texture,
  coreTex: THREE.Texture,
  kind: FxSlot['kind'],
  sizeKm: number,
): FxSlot => {
  const bodyMat = createBodyMaterial(puff);
  const coreMat = createCoreMaterial(coreTex);
  coreMat.color.setHex(EMBER_CORE);
  const body = new THREE.Sprite(bodyMat);
  const core = new THREE.Sprite(coreMat);
  body.visible = false;
  core.visible = false;
  parent.add(body);
  parent.add(core);
  return { body, bodyMat, core, coreMat, sizeKm, kind, ttl: 0, life: 0, jitter: 1, variant: 0, worldPos: vec3() };
};

const activateSlot = (slot: FxSlot, worldPos: Vec3, color: number, seed: number): void => {
  slot.worldPos = { x: worldPos.x, y: worldPos.y, z: worldPos.z };
  slot.life = slot.kind === 'muzzle' ? MUZZLE_LIFE : IMPACT_LIFE;
  slot.ttl = slot.life;
  slot.jitter = 0.85 + hash(seed) * 0.3; // ±15%
  slot.variant = Math.floor(hash(seed * 1.7) * 3);
  slot.bodyMat.color.setHex(color);
  slot.bodyMat.rotation = hash(seed * 2.3) * Math.PI * 2; // per-shot rotation
  slot.body.visible = true;
  slot.core.visible = true;
};

/** Body (matte puff/spall) scale in km for the current life progress. */
const bodyScale = (slot: FxSlot, progress: number): number => {
  if (slot.kind === 'muzzle') return slot.sizeKm * (0.85 + progress * 0.5) * slot.jitter;
  const spread = 0.85 + slot.variant * 0.25; // surface-feel variants
  return slot.sizeKm * (0.3 + progress * spread) * slot.jitter;
};

/** Body opacity — wisp/spall fade out (peak ≤0.7 lives in the texture). */
const bodyOpacity = (slot: FxSlot, progress: number): number => {
  const k = slot.kind === 'muzzle' ? 1.5 : 1.1;
  return Math.max(0, Math.pow(1 - progress, k));
};

/** Tiny additive core: brief flash then gone (muzzle 50% / impact ~0.05s). */
const coreState = (slot: FxSlot, progress: number): { scale: number; opacity: number } => {
  const flashEnd = slot.kind === 'muzzle' ? 0.5 : 0.2; // 0.2 * 0.25s ≈ 0.05s
  const opacity = 0.7 * Math.max(0, 1 - progress / flashEnd);
  const scale = slot.sizeKm * 0.25 * (1 - progress * 0.5); // ≤25% footprint
  return { scale, opacity };
};

export class CombatFxRenderer {
  private readonly muzzlePool: FxSlot[];
  private readonly impactPool: FxSlot[];
  private spawnSeed = 1;

  constructor(parent: THREE.Group) {
    const puff = createPuffTexture();
    const coreTex = createCoreTexture();
    this.muzzlePool = this.buildPool(parent, puff, coreTex, 'muzzle', 8, MUZZLE_SIZE_KM);
    this.impactPool = this.buildPool(parent, puff, coreTex, 'impact', 16, IMPACT_SIZE_KM);
  }

  private buildPool(
    parent: THREE.Group,
    puff: THREE.Texture,
    coreTex: THREE.Texture,
    kind: FxSlot['kind'],
    count: number,
    baseSize: number,
  ): FxSlot[] {
    const pool: FxSlot[] = [];
    for (let i = 0; i < count; i++) {
      pool.push(createSlot(parent, puff, coreTex, kind, baseSize * (0.9 + hash(i + count) * 0.25)));
    }
    return pool;
  }

  spawnMuzzle(worldPos: Vec3, color: number): void {
    activateSlot(this.nextSlot(this.muzzlePool), worldPos, color, this.spawnSeed++);
  }

  spawnImpact(worldPos: Vec3, color: number): void {
    activateSlot(this.nextSlot(this.impactPool), worldPos, color, this.spawnSeed++);
  }

  private nextSlot(pool: FxSlot[]): FxSlot {
    return pool.find((slot) => slot.ttl <= 0) ?? pool[0]!;
  }

  update(dt: number, shipWorldPos: Vec3): void {
    this.stepPool(this.muzzlePool, dt, shipWorldPos);
    this.stepPool(this.impactPool, dt, shipWorldPos);
  }

  private stepPool(pool: readonly FxSlot[], dt: number, shipWorldPos: Vec3): void {
    for (const slot of pool) {
      if (slot.ttl <= 0) {
        slot.body.visible = false;
        slot.core.visible = false;
        continue;
      }
      slot.ttl = Math.max(0, slot.ttl - dt);
      const progress = 1 - slot.ttl / slot.life;
      const dx = slot.worldPos.x - shipWorldPos.x;
      const dy = slot.worldPos.y - shipWorldPos.y;
      const dz = slot.worldPos.z - shipWorldPos.z;
      slot.body.position.set(dx, dy, dz);
      slot.core.position.set(dx, dy, dz);
      slot.body.scale.setScalar(bodyScale(slot, progress));
      slot.bodyMat.opacity = bodyOpacity(slot, progress);
      const core = coreState(slot, progress);
      slot.core.scale.setScalar(core.scale);
      slot.coreMat.opacity = core.opacity;
      slot.body.visible = slot.ttl > 0;
      slot.core.visible = slot.ttl > 0 && core.opacity > 0;
    }
  }
}
